import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Card,
  Form,
  Button,
  Alert,
  Spinner,
  Row,
  Col,
  ListGroup,
  InputGroup
} from 'react-bootstrap';
import { FaArrowLeft, FaPlus, FaStar, FaUserPlus, FaSearch } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { getCustomersSimple, getSitesForCustomer, createCustomer } from '../services/customerService';
import { createSpecialJob } from '../services/jobService';

/**
 * AddSpecialJob Page
 *
 * Creates a "special" job directly without going through the estimate/bid flow.
 * After creation, redirects to the estimate page to add doors via voice transcription.
 */
const AddSpecialJob = () => {
  const navigate = useNavigate();

  // Form state
  const [customerId, setCustomerId] = useState('');
  const [customerName, setCustomerName] = useState('');
  const [siteId, setSiteId] = useState('');
  const [jobScope, setJobScope] = useState('');
  const [region, setRegion] = useState('OC');

  // Data state
  const [sites, setSites] = useState([]);
  const [searchResults, setSearchResults] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);

  // UI state
  const [searchingCustomers, setSearchingCustomers] = useState(false);
  const [loadingSites, setLoadingSites] = useState(false);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [creatingCustomer, setCreatingCustomer] = useState(false);

  // Ref for debounce timeout
  const searchTimeoutRef = useRef(null);

  // Load sites when customer changes
  useEffect(() => {
    if (customerId) {
      loadSites(customerId);
    } else {
      setSites([]);
      setSiteId('');
    }
  }, [customerId]);

  // Server-side customer search with debounce
  const searchCustomersServer = useCallback(async (searchTerm) => {
    if (!searchTerm || searchTerm.trim().length < 1) {
      setSearchResults([]);
      setSearchingCustomers(false);
      return;
    }

    setSearchingCustomers(true);
    try {
      const data = await getCustomersSimple(searchTerm.trim());

      setSearchResults(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error searching customers:', err);
      setSearchResults([]);
    } finally {
      setSearchingCustomers(false);
    }
  }, []);

  const loadSites = async (custId) => {
    setLoadingSites(true);
    setSiteId('');
    try {
      const data = await getSitesForCustomer(custId);
      setSites(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error loading sites:', err);
      setSites([]);
    } finally {
      setLoadingSites(false);
    }
  };

  // Get display name for customer (handles both company_name and name fields)
  const getCustomerDisplayName = (customer) => {
    return customer?.company_name || customer?.name || 'Unknown';
  };

  const handleCustomerSearch = (searchTerm) => {
    setCustomerName(searchTerm);
    setShowDropdown(true);

    // Clear any pending search timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // Clear selection if typing a new name
    if (customerId) {
      setCustomerId('');
    }

    // Debounce the server search (300ms delay)
    if (searchTerm.trim().length >= 1) {
      setSearchingCustomers(true);
      searchTimeoutRef.current = setTimeout(() => {
        searchCustomersServer(searchTerm);
      }, 300);
    } else {
      setSearchResults([]);
      setSearchingCustomers(false);
    }
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  const handleSelectCustomer = (customer) => {
    setCustomerId(customer.id.toString());
    setCustomerName(getCustomerDisplayName(customer));
    setShowDropdown(false);
  };

  const handleCreateNewCustomer = async () => {
    if (!customerName.trim()) {
      setError('Please enter a customer name');
      return;
    }

    setCreatingCustomer(true);
    try {
      const newCustomer = await createCustomer({
        company_name: customerName.trim()
      });

      toast.success(`Customer "${customerName}" created!`);

      // Select the newly created customer
      setCustomerId(newCustomer.id.toString());
      setShowDropdown(false);

    } catch (err) {
      console.error('Error creating customer:', err);
      toast.error('Failed to create customer');
    } finally {
      setCreatingCustomer(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    // If no customer selected but name entered, create customer first
    if (!customerId && customerName.trim()) {
      setCreatingCustomer(true);
      try {
        const newCustomer = await createCustomer({
          company_name: customerName.trim()
        });
        toast.success(`Customer "${customerName}" created!`);
        setCustomerId(newCustomer.id.toString());

        // Now create the job with the new customer
        await createJob(newCustomer.id);
      } catch (err) {
        console.error('Error creating customer:', err);
        setError('Failed to create customer');
        setCreatingCustomer(false);
        return;
      }
      setCreatingCustomer(false);
      return;
    }

    if (!customerId) {
      setError('Please select or enter a customer name');
      return;
    }

    await createJob(parseInt(customerId));
  };

  const createJob = async (custId) => {
    setSubmitting(true);

    try {
      const jobData = {
        customer_id: custId,
        site_id: siteId ? parseInt(siteId) : null,
        job_scope: jobScope,
        region: region
      };

      const result = await createSpecialJob(jobData);

      toast.success(`Special Job #${result.job_number} created successfully!`);

      // Redirect to the job page where they can add items via the modal
      navigate(`/jobs/${result.id}`);

    } catch (err) {
      console.error('Error creating special job:', err);
      setError(err.message || 'Failed to create special job');
      toast.error('Failed to create special job');
    } finally {
      setSubmitting(false);
    }
  };

  // Check if current input matches an existing customer
  const isNewCustomer = customerName.trim() && !customerId;

  return (
    <Container fluid className="py-4" style={{ background: '#f5f7fa', minHeight: '100vh' }}>
      <Row className="justify-content-center">
        <Col md={8} lg={6}>
          {/* Header */}
          <div className="d-flex align-items-center mb-4">
            <Button
              variant="outline-secondary"
              onClick={() => navigate('/jobs')}
              className="me-3"
            >
              <FaArrowLeft className="me-2" />
              Back to Jobs
            </Button>
            <h4 className="mb-0">
              <FaStar className="text-warning me-2" />
              New Special Job
            </h4>
          </div>

          {/* Info Alert */}
          <Alert variant="info" className="mb-4">
            <strong>Special Jobs</strong> bypass the standard estimate/bid workflow.
            After creating the job, you'll be able to add items via voice recording
            and enter pricing directly.
          </Alert>

          {/* Form Card */}
          <Card className="shadow-sm">
            <Card.Header className="bg-primary text-white">
              <h5 className="mb-0">Job Details</h5>
            </Card.Header>
            <Card.Body>
              {error && (
                <Alert variant="danger" dismissible onClose={() => setError(null)}>
                  {error}
                </Alert>
              )}

              <Form onSubmit={handleSubmit}>
                {/* Customer Selection/Input */}
                <Form.Group className="mb-4">
                  <Form.Label className="fw-bold">
                    Customer <span className="text-danger">*</span>
                  </Form.Label>
                  <div className="position-relative">
                    <InputGroup>
                      <InputGroup.Text>
                        <FaSearch />
                      </InputGroup.Text>
                      <Form.Control
                        type="text"
                        placeholder="Search or type new customer name..."
                        value={customerName}
                        onChange={(e) => handleCustomerSearch(e.target.value)}
                        onFocus={() => setShowDropdown(true)}
                        onBlur={() => setTimeout(() => setShowDropdown(false), 200)}
                        autoComplete="off"
                      />
                    </InputGroup>

                    {/* Dropdown for search results */}
                    {showDropdown && customerName.trim() && (
                      <ListGroup
                        className="position-absolute w-100 shadow-sm"
                        style={{ zIndex: 1000, maxHeight: '200px', overflowY: 'auto' }}
                      >
                        {searchingCustomers ? (
                          <ListGroup.Item className="text-center text-muted">
                            <Spinner animation="border" size="sm" className="me-2" />
                            Searching...
                          </ListGroup.Item>
                        ) : searchResults.length > 0 ? (
                          <>
                            {searchResults.slice(0, 10).map(customer => (
                              <ListGroup.Item
                                key={customer.id}
                                action
                                onClick={() => handleSelectCustomer(customer)}
                                active={customerId === customer.id.toString()}
                                style={{ cursor: 'pointer' }}
                              >
                                {getCustomerDisplayName(customer)}
                              </ListGroup.Item>
                            ))}
                            {/* Show create option if typed name doesn't exactly match */}
                            {!searchResults.find(c =>
                              getCustomerDisplayName(c).toLowerCase() === customerName.toLowerCase()
                            ) && (
                              <ListGroup.Item
                                action
                                onClick={handleCreateNewCustomer}
                                className="text-success border-top"
                                style={{ cursor: 'pointer' }}
                              >
                                <FaUserPlus className="me-2" />
                                Create new customer: "{customerName}"
                              </ListGroup.Item>
                            )}
                          </>
                        ) : (
                          <ListGroup.Item
                            action
                            onClick={handleCreateNewCustomer}
                            className="text-success"
                            style={{ cursor: 'pointer' }}
                          >
                            <FaUserPlus className="me-2" />
                            Create new customer: "{customerName}"
                          </ListGroup.Item>
                        )}
                      </ListGroup>
                    )}
                  </div>

                  {/* Show selected customer or new customer indicator */}
                  {customerId && (
                    <Form.Text className="text-success">
                      Selected: {customerName}
                    </Form.Text>
                  )}
                  {isNewCustomer && (
                    <Form.Text className="text-info">
                      <FaUserPlus className="me-1" />
                      New customer will be created: "{customerName}"
                    </Form.Text>
                  )}
                </Form.Group>

                {/* Site Selection (Optional) */}
                <Form.Group className="mb-4">
                  <Form.Label className="fw-bold">
                    Site <span className="text-muted">(Optional)</span>
                  </Form.Label>
                  {loadingSites ? (
                    <div className="text-center py-3">
                      <Spinner animation="border" size="sm" />
                      <span className="ms-2">Loading sites...</span>
                    </div>
                  ) : sites.length > 0 ? (
                    <Form.Select
                      value={siteId}
                      onChange={(e) => setSiteId(e.target.value)}
                    >
                      <option value="">No specific site</option>
                      {sites.map(site => (
                        <option key={site.id} value={site.id}>
                          {site.name || site.address} {site.name && site.address ? `- ${site.address}` : ''}
                        </option>
                      ))}
                    </Form.Select>
                  ) : customerId ? (
                    <Form.Control
                      type="text"
                      disabled
                      placeholder="No sites available for this customer"
                    />
                  ) : (
                    <Form.Control
                      type="text"
                      disabled
                      placeholder="Select a customer first"
                    />
                  )}
                </Form.Group>

                {/* Region Selection */}
                <Form.Group className="mb-4">
                  <Form.Label className="fw-bold">Region</Form.Label>
                  <Form.Select
                    value={region}
                    onChange={(e) => setRegion(e.target.value)}
                  >
                    <option value="OC">Orange County (OC)</option>
                    <option value="LA">Los Angeles (LA)</option>
                    <option value="IE">Inland Empire (IE)</option>
                  </Form.Select>
                </Form.Group>

                {/* Job Scope */}
                <Form.Group className="mb-4">
                  <Form.Label className="fw-bold">
                    Job Description <span className="text-muted">(Optional)</span>
                  </Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    value={jobScope}
                    onChange={(e) => setJobScope(e.target.value)}
                    placeholder="Brief description of the job..."
                  />
                  <Form.Text className="text-muted">
                    You can add more details and items via voice recording after creating the job.
                  </Form.Text>
                </Form.Group>

                {/* Submit Button */}
                <div className="d-grid gap-2">
                  <Button
                    type="submit"
                    variant="primary"
                    size="lg"
                    disabled={submitting || creatingCustomer || (!customerId && !customerName.trim())}
                  >
                    {submitting || creatingCustomer ? (
                      <>
                        <Spinner animation="border" size="sm" className="me-2" />
                        {creatingCustomer ? 'Creating Customer...' : 'Creating Job...'}
                      </>
                    ) : (
                      <>
                        <FaPlus className="me-2" />
                        {isNewCustomer ? 'Create Customer & Job' : 'Create Special Job & Add Items'}
                      </>
                    )}
                  </Button>
                </div>
              </Form>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default AddSpecialJob;
